import { Injectable } from '@angular/core';
import {
  AlertController,
  AlertOptions,
  LoadingController,
  ModalController,
  ModalOptions,
  PopoverController
} from '@ionic/angular';
import { OverlayEventDetail } from '@ionic/core';
import { Actions, ofActionCompleted } from '@ngxs/store';
import _ from 'lodash';
import { BehaviorSubject, Observable, Subject, take, takeUntil } from 'rxjs';

import { MenuConstant } from 'src/app/constants/menu.constant';
import { GetMenuTally } from 'src/app/store/menu/menu.action';
import {
  ModifierPromptOption,
  POSModifierPromptAssignment
} from '../../models';
import { TranslocoService } from '@ngneat/transloco';

// const DEFAULT_MODAL_OPTS: Partial<ModalOptions> = {
//   cssClass: 'pos-modal',
//   backdropDismiss: false
// };

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  get showingModals$(): Observable<HTMLIonModalElement[]> {
    return this.showingModalSubject.asObservable();
  }

  private showingModalSubject = new BehaviorSubject<HTMLIonModalElement[]>([]);

  private get showingModals(): HTMLIonModalElement[] {
    return this.showingModalSubject.getValue();
  }

  private addShowingModal(modal: HTMLIonModalElement) {
    this.showingModalSubject.next(this.showingModals.concat(modal));
  }

  private removeShowingModal(modal: HTMLIonModalElement) {
    this.showingModalSubject.next(
      this.showingModals.filter((m) => m.id !== modal.id)
    );
  }

  private readonly _isLoading$ = new BehaviorSubject(false);
  readonly isLoading$ = this._isLoading$.asObservable();

  readonly MODAL_DESTROYED_PROPERTY = 'modalDestroyed';

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly popoverCtrl: PopoverController,
    private readonly alertCtrl: AlertController,
    private readonly loadingCtrl: LoadingController,
    private readonly actions$: Actions,
    private readonly translocoService: TranslocoService
  ) {}

  private set isLoading(value: boolean) {
    this._isLoading$.next(value);
  }

  private get isLoading(): boolean {
    return this._isLoading$.value;
  }

  /**
   * @param T_K T is the result data type when modal is dismissed, K is the props type to be passed into the modal
   * @param modalComponent
   * @param data
   * @param opts
   * @param dismissHandler
   * @param blockDuplicate
   * @returns
   */
  async showModal<T = any, K = _.Dictionary<any>>(
    modalComponent: any,
    data?: K,
    opts?: Partial<ModalOptions>,
    dismissHandler?: (data: { data?: T; role?: string }) => void,
    blockDuplicate = false
  ) {
    if (!opts) {
      opts = { backdropDismiss: false };
    }
    const modalOpts: ModalOptions = {
      component: modalComponent,
      componentProps: data,
      cssClass: 'pos-modal',
      id: modalComponent.name,
      animated: false,
      ...opts
    };

    if (blockDuplicate) {
      const existingModal = this.showingModals.find(
        (item) => item.id === modalOpts.id
      );

      if (!!existingModal) {
        return existingModal;
      }
    }

    const modal = await this.modalCtrl.create(modalOpts);

    modal.onWillDismiss().then((v) => {
      this.removeShowingModal(modal);

      if (dismissHandler) {
        dismissHandler(v);
      }
    });

    modal.present().then(() => {
      if (
        opts.hasOwnProperty(this.MODAL_DESTROYED_PROPERTY) &&
        opts[this.MODAL_DESTROYED_PROPERTY]
      ) {
        modal.dismiss().catch(() => {});
      }
    });
    this.addShowingModal(modal);
    return modal;
  }

  async showLoadingModal(
    message = 'Loading data...'
  ): Promise<HTMLIonLoadingElement> {
    if (this.isLoading) {
      await this.loadingCtrl.dismiss().catch(() => {});
    }

    this.isLoading = true;
    const loading = await this.loadingCtrl.create({
      message,
      id: new Date().getTime().toString()
    });
    const loadingModalId = loading.id;
    await loading.present().then(() => {
      if (!this.isLoading) {
        this.closeLoadingModal(loadingModalId);
      } else {
        setTimeout(() => {
          this.closeLoadingModal(loadingModalId);
        }, 60000);
      }
    }).catch((error) => {
      console.error('Error presenting loading:', error);
    });

    return loading;
  }

  async closeLoadingModal(loadingModalId: string): Promise<void> {
    this.isLoading = false;
    await this.loadingCtrl.dismiss({}, '', loadingModalId).catch(() => { /* ignore dismissal errors */ });
  }

  async alert(
    opts?: AlertOptions,
    willDismissHandler?: (data: OverlayEventDetail<any>) => void
  ) {
    const alert = await this.alertCtrl.create({
      ...opts
    });
    await alert.present();
    if (willDismissHandler) {
      alert.onWillDismiss().then(willDismissHandler);
    }
  }

  async closeAllModals() {
    for (const modal of this.showingModals) {
      await modal.dismiss().catch(() => {});
    }
    // try to close the modal which are opened by modal controller instead of modal service
    await this.modalCtrl.dismiss().catch(() => {});
    await this.popoverCtrl.dismiss().catch(() => {});
    await this.alertCtrl.dismiss().catch(() => {});
    await this.loadingCtrl.dismiss().catch(() => {});
    this.showingModalSubject.next([]); // Clear the showing modals
  }

  async isSomeModalOpen(): Promise<boolean> {
    return !_.isEmpty(
      _.compact([
        await this.modalCtrl.getTop(),
        await this.popoverCtrl.getTop(),
        await this.alertCtrl.getTop(),
        await this.loadingCtrl.getTop()
      ])
    );
  }

  alertErrorMessage(message: string) {
    this.alertCtrl
      .create({
        message,
        header: this.translocoService.translate('common.error'),
        backdropDismiss: false,
        cssClass: 'alert-message',
        buttons: [
          {
            text: this.translocoService.translate('common.ok'),
            role: 'ok'
          }
        ]
      })
      .then((alert) => alert.present());
  }

  /**
   * Displays a loading modal indicating that the menu tally is being loaded.
   * If loading exceeds a timeout, a longer loading message is shown.
   * @returns {Promise<void>} Resolves when the loading modal is closed.
   */
  async showMenuTallyLoadingModal() {
    let tallyLoadingModalId = (
      await this.showLoadingModal(
        MenuConstant.LOADING_MODAL_MSG.MENU_TALLY_LOADING
      )
    ).id;

    const TIMEOUT_MS = 5000,
      destroy$ = new Subject<void>(),
      timeout = setTimeout(async () => {
        destroy$.next();
        await this.closeLoadingModal(tallyLoadingModalId);
        tallyLoadingModalId = (
          await this.showLoadingModal(
            MenuConstant.LOADING_MODAL_MSG.MENU_TALLY_LOADING_LONGER
          )
        ).id;
      }, TIMEOUT_MS);

    this.actions$
      .pipe(ofActionCompleted(GetMenuTally), takeUntil(destroy$), take(1))
      .subscribe(() => {
        clearTimeout(timeout);
        this.closeLoadingModal(tallyLoadingModalId).catch(error => console.error(error));
      });
  }

  isValidPrompt(prompt: POSModifierPromptAssignment): boolean {
    const selectedCount = prompt.selectedOptions?.length || 0;
    if (!prompt.min && !prompt.max) {
      return true; // no min - no max => always valid
    }

    const min = prompt.min || 0;
    const max = prompt.max || 999999;

    if (min > selectedCount) {
      return false;
    }
    if (max < selectedCount) {
      return false;
    }
    return true;
  }

  onSelect(
    selectedPrompts: POSModifierPromptAssignment[],
    prompt: POSModifierPromptAssignment,
    option: ModifierPromptOption
  ) {
    const existedPrompt = selectedPrompts.find((x) => x.id === prompt.id);
    existedPrompt.isTouched = true;
    const existedOption = existedPrompt.selectedOptions?.find(
      (x) => x.id === option.id
    );
    if (existedPrompt.max === 1 && existedPrompt.min === 1) {
      // should remove everything in this list as it's radio button
      existedPrompt.selectedOptions = [_.cloneDeep(option)];
    } else {
      if (existedOption) {
        existedPrompt.selectedOptions = existedPrompt.selectedOptions.filter(
          (x) => x.id !== option.id
        );
      } else {
        existedPrompt.selectedOptions = existedPrompt.selectedOptions || [];
        existedPrompt.selectedOptions.push(_.cloneDeep(option));
      }
    }
    existedPrompt.isValid = this.isValidPrompt(existedPrompt);
  }

  checkIsFormValid(selectedPrompts: POSModifierPromptAssignment[]) {
    return selectedPrompts.every((x) => x.isValid);
  }
}
